import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";

/* ---------- helpers ---------- */
const inr = (n) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const twoDigits = (n) => (n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? " " + ones[n % 10] : ""}`);
const threeDigits = (n) => {
  const h = Math.floor(n / 100), r = n % 100;
  return `${h ? ones[h] + " Hundred" + (r ? " " : "") : ""}${r ? twoDigits(r) : ""}`;
};
const numToWords = (num) => {
  num = Math.floor(num);
  if (num === 0) return "Zero";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const rest = num;
  let out = "";
  if (crore) out += threeDigits(crore) + " Crore ";
  if (lakh) out += threeDigits(lakh) + " Lakh ";
  if (thousand) out += threeDigits(thousand) + " Thousand ";
  if (rest) out += threeDigits(rest);
  return out.trim();
};

/* ---------- invoice data (sample) ---------- */
const GST_RATE = 0.18; // 9% CGST + 9% SGST
const items = [
  { desc: "Satyanarayana Pooja — Purohith performance & Dakshina", detail: "Verified purohith · 1 purohith · approx. 2.5 hrs · Smartha vidhi", sac: "999799", qty: 1, unit: "Service", rate: 5000 },
  { desc: "Pooja Samagri Kit (purohith-provided)", detail: "Complete samagri — kalasha items, prasada, flowers, naivedya provisions", sac: "999799", qty: 1, unit: "Kit", rate: 1800 },
  { desc: "Purohith Travel & Conveyance", detail: "To-and-fro within Bengaluru city limits", sac: "996601", qty: 1, unit: "Trip", rate: 700 },
];

export default function InvoiceSample() {
  const hideChrome = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("print") === "1";
  const calc = useMemo(() => {
    const rows = items.map((it) => {
      const taxable = it.qty * it.rate;
      const cgst = +(taxable * (GST_RATE / 2)).toFixed(2);
      const sgst = +(taxable * (GST_RATE / 2)).toFixed(2);
      return { ...it, taxable, cgst, sgst, amount: +(taxable + cgst + sgst).toFixed(2) };
    });
    const taxable = rows.reduce((s, r) => s + r.taxable, 0);
    const cgst = +rows.reduce((s, r) => s + r.cgst, 0).toFixed(2);
    const sgst = +rows.reduce((s, r) => s + r.sgst, 0).toFixed(2);
    const grand = Math.round(taxable + cgst + sgst);
    const roundOff = +(grand - (taxable + cgst + sgst)).toFixed(2);
    return { rows, taxable, cgst, sgst, grand, roundOff };
  }, []);

  return (
    <div className={`min-h-screen bg-[#EFE7DA] print:bg-white print:py-0 ${hideChrome ? "py-0" : "py-6"}`} style={{ colorScheme: "light" }}>
      {/* print rules */}
      <style>{`
        @page { size: A4; margin: 14mm; }
        @media print {
          .no-print { display: none !important; }
          .invoice-sheet { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; }
        }
      `}</style>

      {/* action bar */}
      {!hideChrome && (
      <div className="no-print mx-auto mb-5 flex max-w-[820px] items-center justify-between px-4">
        <Link to="/" data-testid="invoice-back-link" className="inline-flex items-center gap-2 font-mukta text-sm font-semibold text-[#8B1A2B] hover:text-[#6f1420]">
          <ArrowLeft size={16} /> Back to PurohithConnect™
        </Link>
        <button onClick={() => window.print()} data-testid="invoice-print-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#B23A2B] px-5 py-2.5 font-mukta text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#9a2f22]">
          <Printer size={16} /> Print / Download PDF
        </button>
      </div>
      )}

      {/* invoice sheet */}
      <div data-testid="invoice-sheet" className="invoice-sheet mx-auto max-w-[820px] bg-white px-8 py-8 text-[#2A211C] shadow-xl print:max-w-none sm:px-10">

        {/* header */}
        <div className="flex flex-col gap-6 border-b-2 border-[#8B1A2B]/15 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <img src="/brand/pc-logo-full.png" alt="PurohithConnect™" className="h-28 w-auto" data-testid="invoice-logo" />
          </div>
          <div className="text-left sm:text-right">
            <h1 className="font-eczar text-3xl font-bold tracking-wide text-[#8B1A2B]">TAX INVOICE</h1>
            <p className="mt-1 font-mukta text-xs uppercase tracking-widest text-[#A8722A]">Original for Recipient</p>
            <div className="mt-3 space-y-0.5 font-mukta text-sm">
              <p><span className="text-[#7A6E64]">Invoice No:</span> <span className="font-semibold" data-testid="invoice-number">PC/2026-27/0001</span></p>
              <p><span className="text-[#7A6E64]">Invoice Date:</span> <span className="font-semibold">15 Jun 2026</span></p>
              <p><span className="text-[#7A6E64]">Due Date:</span> <span className="font-semibold">15 Jun 2026</span></p>
              <p><span className="text-[#7A6E64]">Booking ID:</span> <span className="font-semibold">BKG-SAT-28461</span></p>
            </div>
          </div>
        </div>

        {/* seller / buyer */}
        <div className="grid grid-cols-1 gap-6 border-b border-[#EBE0D0] py-6 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <p className="mb-1 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Billed By</p>
            <p className="font-eczar text-base font-bold text-[#8B1A2B]">PurohithConnect™</p>
            <p className="font-mukta text-sm text-[#5C5149]">Purohith Connect Technologies Pvt Ltd</p>
            <p className="mt-1 font-mukta text-xs leading-relaxed text-[#7A6E64]">
              [Registered Address Line 1]<br />
              [Area, Bengaluru], Karnataka – [560XXX]<br />
              <span className="font-semibold text-[#5C5149]">GSTIN:</span> 29ABCDE1234F1Z5<br />
              <span className="font-semibold text-[#5C5149]">PAN:</span> ABCDE1234F<br />
              <span className="font-semibold text-[#5C5149]">Phone:</span> +91 90000 00000<br />
              <span className="font-semibold text-[#5C5149]">Email:</span> billing@purohithconnect.com<br />
              <span className="font-semibold text-[#5C5149]">Web:</span> purohithconnect.com
            </p>
          </div>
          <div className="sm:col-span-1">
            <p className="mb-1 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Billed To</p>
            <p className="font-eczar text-base font-bold text-[#2A211C]">Sri Ramesh Kumar</p>
            <p className="mt-1 font-mukta text-xs leading-relaxed text-[#7A6E64]">
              No. 142, 3rd Cross, Jayanagar 4th Block<br />
              Bengaluru, Karnataka – 560011<br />
              <span className="font-semibold text-[#5C5149]">Phone:</span> +91 98xxx xxxxx<br />
              <span className="font-semibold text-[#5C5149]">GSTIN:</span> Unregistered (B2C)
            </p>
          </div>
          <div className="sm:col-span-1">
            <p className="mb-1 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Service Details</p>
            <p className="font-mukta text-xs leading-relaxed text-[#7A6E64]">
              <span className="font-semibold text-[#5C5149]">Place of Supply:</span> Karnataka (29)<br />
              <span className="font-semibold text-[#5C5149]">Ceremony:</span> Satyanarayana Pooja<br />
              <span className="font-semibold text-[#5C5149]">Date of Service:</span> 15 Jun 2026, 10:00 AM<br />
              <span className="font-semibold text-[#5C5149]">Venue:</span> Customer residence, Jayanagar<br />
              <span className="font-semibold text-[#5C5149]">Payment Mode:</span> UPI (Online)
            </p>
          </div>
        </div>

        {/* line items */}
        <div className="overflow-x-auto py-6">
          <table className="w-full border-collapse text-left font-mukta text-sm" data-testid="invoice-items-table">
            <thead>
              <tr className="bg-[#8B1A2B] text-white">
                <th className="rounded-l-md px-3 py-2.5 text-xs font-semibold">#</th>
                <th className="px-3 py-2.5 text-xs font-semibold">Description</th>
                <th className="px-3 py-2.5 text-xs font-semibold">SAC</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Qty</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Rate (₹)</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Taxable (₹)</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">CGST 9%</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">SGST 9%</th>
                <th className="rounded-r-md px-3 py-2.5 text-right text-xs font-semibold">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {calc.rows.map((r, i) => (
                <tr key={i} className="border-b border-[#EBE0D0] align-top" data-testid={`invoice-row-${i}`}>
                  <td className="px-3 py-3 text-[#7A6E64]">{i + 1}</td>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-[#2A211C]">{r.desc}</p>
                    <p className="mt-0.5 text-xs text-[#7A6E64]">{r.detail}</p>
                  </td>
                  <td className="px-3 py-3 text-[#5C5149]">{r.sac}</td>
                  <td className="px-3 py-3 text-right text-[#5C5149]">{r.qty} {r.unit}</td>
                  <td className="px-3 py-3 text-right text-[#5C5149]">{inr(r.rate)}</td>
                  <td className="px-3 py-3 text-right text-[#5C5149]">{inr(r.taxable)}</td>
                  <td className="px-3 py-3 text-right text-[#5C5149]">{inr(r.cgst)}</td>
                  <td className="px-3 py-3 text-right text-[#5C5149]">{inr(r.sgst)}</td>
                  <td className="px-3 py-3 text-right font-semibold text-[#2A211C]">{inr(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* totals + tax summary */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* tax summary */}
          <div>
            <p className="mb-2 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Tax Summary</p>
            <table className="w-full border-collapse font-mukta text-xs">
              <thead>
                <tr className="border-b border-[#EBE0D0] text-[#7A6E64]">
                  <th className="py-1.5 text-left font-semibold">Taxable Value</th>
                  <th className="py-1.5 text-right font-semibold">CGST (9%)</th>
                  <th className="py-1.5 text-right font-semibold">SGST (9%)</th>
                  <th className="py-1.5 text-right font-semibold">Total Tax</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-1.5 text-[#5C5149]">₹{inr(calc.taxable)}</td>
                  <td className="py-1.5 text-right text-[#5C5149]">₹{inr(calc.cgst)}</td>
                  <td className="py-1.5 text-right text-[#5C5149]">₹{inr(calc.sgst)}</td>
                  <td className="py-1.5 text-right font-semibold text-[#2A211C]">₹{inr(calc.cgst + calc.sgst)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-4 rounded-lg bg-[#F7F1E7] p-3">
              <p className="font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Amount in Words</p>
              <p className="mt-0.5 font-mukta text-sm font-semibold text-[#2A211C]" data-testid="invoice-amount-words">
                Indian Rupees {numToWords(calc.grand)} Only
              </p>
            </div>
          </div>

          {/* totals */}
          <div className="font-mukta text-sm">
            <div className="flex justify-between py-1.5 text-[#5C5149]">
              <span>Subtotal (Taxable Value)</span><span>₹{inr(calc.taxable)}</span>
            </div>
            <div className="flex justify-between py-1.5 text-[#5C5149]">
              <span>CGST @ 9%</span><span>₹{inr(calc.cgst)}</span>
            </div>
            <div className="flex justify-between py-1.5 text-[#5C5149]">
              <span>SGST @ 9%</span><span>₹{inr(calc.sgst)}</span>
            </div>
            <div className="flex justify-between py-1.5 text-[#5C5149]">
              <span>Round Off</span><span>₹{inr(calc.roundOff)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between rounded-lg bg-[#8B1A2B] px-4 py-3 text-white" data-testid="invoice-grand-total">
              <span className="font-eczar text-base font-bold">Grand Total</span>
              <span className="font-eczar text-xl font-bold">₹{inr(calc.grand)}</span>
            </div>
            <div className="mt-2 flex justify-end">
              <span className="rounded-full bg-[#1E7F43]/10 px-3 py-1 font-mukta text-xs font-bold uppercase tracking-wider text-[#1E7F43]" data-testid="invoice-status">
                Paid · UPI
              </span>
            </div>
          </div>
        </div>

        {/* payment + terms */}
        <div className="mt-6 grid grid-cols-1 gap-6 border-t border-[#EBE0D0] pt-6 sm:grid-cols-2">
          <div>
            <p className="mb-1 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Payment Details</p>
            <p className="font-mukta text-xs leading-relaxed text-[#7A6E64]">
              <span className="font-semibold text-[#5C5149]">Bank:</span> [Bank Name]<br />
              <span className="font-semibold text-[#5C5149]">A/C Name:</span> Purohith Connect Technologies Pvt Ltd<br />
              <span className="font-semibold text-[#5C5149]">A/C No:</span> [XXXXXXXXXXXX]<br />
              <span className="font-semibold text-[#5C5149]">IFSC:</span> [XXXX0XXXXXX]<br />
              <span className="font-semibold text-[#5C5149]">UPI:</span> purohithconnect@[upi]
            </p>
          </div>
          <div>
            <p className="mb-1 font-mukta text-[11px] font-bold uppercase tracking-wider text-[#A8722A]">Terms & Notes</p>
            <ul className="list-disc space-y-1 pl-4 font-mukta text-xs leading-relaxed text-[#7A6E64]">
              <li>Dakshina is offered with respect to the purohith for performing the ceremony.</li>
              <li>Samagri charges apply only when purohith-provided samagri is selected.</li>
              <li>Prices are inclusive of the itemised amounts above; there are no hidden charges.</li>
              <li>Cancellations 24+ hrs before the muhurta are fully refundable (excl. gateway fees).</li>
            </ul>
          </div>
        </div>

        {/* signature + footer */}
        <div className="mt-8 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <p className="font-baloo text-lg text-[#A8722A]">|| ಶುಭಮಸ್ತು ||</p>
          <div className="text-right">
            <div className="mb-2 h-10" />
            <p className="border-t border-[#2A211C]/30 pt-1 font-mukta text-xs font-semibold text-[#2A211C]">Authorised Signatory</p>
            <p className="font-mukta text-xs text-[#7A6E64]">for PurohithConnect™</p>
          </div>
        </div>

        <p className="mt-6 border-t border-[#EBE0D0] pt-3 text-center font-mukta text-[11px] text-[#9A8E82]">
          This is a computer-generated invoice and does not require a physical signature. · PurohithConnect™ — Your Trusted Partner for Sacred Occasions · purohithconnect.com
        </p>
      </div>
    </div>
  );
}
